import React, { useState } from 'react';
import { Box, Skeleton, Tooltip, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { styled } from '@mui/system';
import VatTable from './VatTable';
import AdvancesTable from './AdvancesTable';
import FinanceStatCard from './FinanceStatCard';
import StatusOverviewChart from './StatusOverviewChart';
import PropTypes from 'prop-types';
import FilterMonthYearPicker from '../../../Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker';
import BiMonthlyPeriodModal from '../../Modals/BiMonthlyPeriodModal';
import HeaderToolbarStyles from '../ClientDetailsHeader/HeaderToolbar.module.scss';
import { Receipt, AccountBalance } from '@mui/icons-material';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { SALARY_SOURCE } from '../../../../Services/salaries.service';
import YearlyTaxAdvanceModal from '../../../Widgets/YearlyTaxAdvanceModal/YearlyTaxAdvanceModal';
import SendPaymentMessageModal from '../../Modals/SendPaymentMessageModal';
import WhatsAppMessagesService from '../../../../Services/whatsapp-messages.service';
import { formatDate_DD_MM_YYYY } from '../../../../Utils/FormatDate';
import { toast } from 'react-toastify';
import './PanelsCards.css';

const PartnerSelectorContainer = styled(Box)({
  marginBottom: '12px',
  marginTop: '4px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
});

const StyledFormControl = styled(FormControl)({
  width: '100%',
  maxWidth: '280px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    minHeight: 'var(--xtram-control-height-sm, 32px)',
    fontSize: 'var(--xtram-panel-cell, 12px)',
    '& fieldset': {
      borderColor: '#dfe7f1',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#bbd0ff',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3366ff',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#53657e',
    fontWeight: 600,
    fontSize: 'var(--xtram-panel-head, 11px)',
    '&.Mui-focused': {
      color: '#3366ff',
    },
  },
  '& .MuiSelect-select': {
    fontSize: 'var(--xtram-panel-cell, 12px)',
  },
});

const VatAdvancesSection = ({
  fetchLoading,
  client,
  direction,
  headerStyle,
  t,
  beautifyNumber,
  netVatAmount,
  incomesVatDetailed,
  expensesVatDetailed,
  equipmentVatDetailed,
  purchaseOfEquipmentExpensesVatAmount,
  expensesVatAmount,
  incomesVatAmount,
  reportTax,
  downloadVATReport,
  permissionsState,
  userTypeContext,
  handleShowVatModal,
  VatTooltip,
  calculatedValues,
  witholdingIncomesNoVat,
  downloadWithholdingTaxReport,
  advanceReportTax,
  handleOpenWithholdingTaxModal,
  Constants,
  // Date-related props
  isRange,
  isBiMonthly,
  isAdvancedBiMonthly,
  date,
  startDate,
  endDate,
  biStartDate,
  biEndDate,
  advancedDate,
  advancedStartDate,
  advancedEndDate,
  advancedBiStartDate,
  advancedBiEndDate,
  showBiMonth,
  setShowBiMonth,
  handleDateChangeNew,
  handleStartDateChangeNew,
  handleEndDateChangeNew,
  handleBiMonthDate,
  handleAdvancedDateChangeNew,
  handleAdvancedStartDateChangeNew,
  handleAdvancedEndDateChangeNew,
  handleAdvancedBiMonthDate,
  formatDateToYearMonth,
  // Additional props for FinancialModal and ActionCards
  incomesAmount,
  expensesAmount,
  purchaseOfEquipmentExpensesAmount,
  profitAmount,
  paymentWithholdingTax,
  viewDocumentDetails,
  viewSortCodeReport,
  viewPaymentAcceptanceDetails,
  viewAdvanceCalculator,
  handleRangeChange,
  // New props for withholding tax
  withholdingTaxPercentage,
  withholdingTaxApprovalValidUntil,
  // Additional props for SortCodeReport navigation
  isMonthly,
  startYearDate,
  endYearDate,
  // Partners props
  token,
  partners = [],
  calculatePartnerValues,
  partnerCalculatedValues,
  // Shared case props
  isSubordinateClient = false,
  mainClientData = null,
  isMainWithSubordinates = false,
  subordinateClients = [],
  hideVatPanel = false,
  // Chat
  handleSendTextMessageToChat,
  accountantName = '',
  onlyProcessedDocuments = false,
  // National Insurance panel
  showNationalInsurancePanel = false,
  nationalInsuranceSummary = null,
  nationalInsurancePeriodLabel = '',
  onOpenNationalInsurance,
  onOpenReportingFollowUp,
  // Salary panel
  showSalaryPanel = false,
  salaryOverview = null,
  salaryLoading = false,
  salaryPeriodLabel = '',
  onOpenSalary,
}) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState('all');
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [yearlyTaxAdvanceModalOpen, setYearlyTaxAdvanceModalOpen] = useState(false);
  // 'vat' or 'advances' (null = closed)
  const [sendPaymentMessageMode, setSendPaymentMessageMode] = useState(null);


  const handlePartnerChange = (event) => {
    setSelectedPartnerId(event.target.value);
  };

  const handleOpenWithholdingTaxModalWithPartner = (selectedPartner) => {
    // Pass the selected partner to the parent component
    if (typeof handleOpenWithholdingTaxModal === 'function') {
      handleOpenWithholdingTaxModal(selectedPartner);
    }
  };

  const formatPeriod = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${month}/${year}`;
  };

  const computeVatPeriodLabel = () => {
    if (isRange) {
      return `${formatPeriod(startDate)} - ${formatPeriod(endDate)}`;
    }
    if (isBiMonthly) {
      return `${formatPeriod(biStartDate)} - ${formatPeriod(biEndDate)}`;
    }
    return formatPeriod(date);
  };

  /** Anchor: VAT period start — chart returns N months immediately before this. */
  const chartStartDate = (() => {
    if (isRange) return startDate;
    if (isBiMonthly) return biStartDate;
    return date;
  })();

  /** VAT period end — sent for parity with GetVatAdvancesPageData (optional on chart API). */
  const chartEndDate = (() => {
    if (isRange) return endDate;
    if (isBiMonthly) return biEndDate;
    return date;
  })();

  const computeAdvancesPeriodLabel = () => {
    const useAdvanced = isAdvancedBiMonthly !== isBiMonthly;
    if (isRange) {
      return useAdvanced
        ? `${formatPeriod(advancedStartDate)} - ${formatPeriod(advancedEndDate)}`
        : `${formatPeriod(startDate)} - ${formatPeriod(endDate)}`;
    }
    if (useAdvanced) {
      if (isAdvancedBiMonthly) {
        return `${formatPeriod(advancedBiStartDate)} - ${formatPeriod(advancedBiEndDate)}`;
      }
      return formatPeriod(advancedDate);
    }
    if (isBiMonthly) {
      return `${formatPeriod(biStartDate)} - ${formatPeriod(biEndDate)}`;
    }
    return formatPeriod(date);
  };

  const handleOpenSendPaymentMessage = (mode) => {
    setSendPaymentMessageMode(mode);
  };

  const handleSendPaymentMessage = async (message) => {
    if (typeof handleSendTextMessageToChat !== 'function') {
      toast.error(t('ErrorSendingMessageToChat'));
      return;
    }
    if (!client?.caseId) {
      toast.error(t('ErrorSendingMessageToChat'));
      return;
    }
    await handleSendTextMessageToChat(message, client.caseId, client.name || '');
  };

  const handleSendPaymentMessageWhatsApp = async (payload) => {
    await WhatsAppMessagesService.sendPaymentReminder(payload);
  };

  const handleDownloadWithholdingTaxReportWithPartner = (partnerIdFromParam = null) => {
    // If a partnerId is passed as parameter (from Swal dialog), use it
    // Otherwise, use the component's selectedPartnerId state
    let partnerIdToUse = partnerIdFromParam;
    
    if (!partnerIdToUse) {
      // Get the selected partner or null if "all" is selected
      const partnerToDownload = selectedPartnerId === 'all' ? null : partners.find(p => p.id === selectedPartnerId);
      partnerIdToUse = partnerToDownload?.id || null;
    }
    
    if (typeof downloadWithholdingTaxReport === 'function') {
      downloadWithholdingTaxReport(partnerIdToUse);
    }
  };
  if (fetchLoading || !client) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} />
      </Box>
    );
  }

  const caseTypeRaw = (client?.caseType ?? client?.CaseType ?? '').trim();
  const isTaxExemptCase = Constants?.TaxExemptTypes?.includes(caseTypeRaw);
  const showVatPanelForExemptClient = Boolean(
    client?.showVatPanelForTaxExempt ?? client?.ShowVatPanelForTaxExempt
  );
  // Tax-exempt: VAT panel always hidden. Advances panel hidden unless they opted in.
  // Non-exempt: respect parent's hideVatPanel for the VAT panel; advances always shown.
  const hideVatColumn = isTaxExemptCase ? true : !!hideVatPanel;
  const hideAdvancesColumn = isTaxExemptCase && !showVatPanelForExemptClient;

  const WithholdingTaxPercentage = Math.max(0, withholdingTaxPercentage * 100).toFixed(1);

  const canSendChatMessage =
    !isSubordinateClient &&
    userTypeContext?.toString() !== Constants?.UserType?.Client;

  const activePartner = selectedPartnerId === 'all'
    ? null
    : partners.find((p) => p.id === selectedPartnerId);

  const partnerBusinessTurnover = activePartner
    ? (
      partnerCalculatedValues[activePartner.id]?.computed?.witholdingIncomesNoVat
      ?? (witholdingIncomesNoVat || 0) * (activePartner.equity / 100)
    )
    : (witholdingIncomesNoVat || 0);

  const partnerAcceptedWithholdingTotal = activePartner
    ? (
      partnerCalculatedValues[activePartner.id]?.computed?.AcceptedWithholdingTaxMaximum
      ?? (calculatedValues?.computed?.AcceptedWithholdingTaxMaximum || 0) * (activePartner.equity / 100)
    )
    : partners.reduce((total, partner) => {
      const partnerValues = partnerCalculatedValues[partner.id];
      const partnerAcceptedWithholdingTax = partnerValues?.computed?.AcceptedWithholdingTaxMaximum
        || (calculatedValues?.computed?.AcceptedWithholdingTaxMaximum || 0) * (partner.equity / 100);
      return total + partnerAcceptedWithholdingTax;
    }, 0);

  const showPanelDates = isAdvancedBiMonthly !== isBiMonthly;
  const tb = HeaderToolbarStyles;
  const withholdingValue = withholdingTaxPercentage
    ? `${withholdingTaxPercentage}%`
    : `${WithholdingTaxPercentage}%`;
  const withholdingExpired =
    withholdingTaxApprovalValidUntil && new Date(withholdingTaxApprovalValidUntil) < new Date();
  const oneMonthAhead = new Date();
  oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);
  const withholdingExpiresWithinMonth =
    withholdingTaxApprovalValidUntil
    && !withholdingExpired
    && new Date(withholdingTaxApprovalValidUntil) <= oneMonthAhead;
  const withholdingSubtitle = !withholdingExpired && !withholdingExpiresWithinMonth
    ? (
      withholdingTaxApprovalValidUntil
        ? `${t('WithholdingTaxApprovalValidity')}: ${formatDate_DD_MM_YYYY(withholdingTaxApprovalValidUntil)}`
        : t('NoApprovalDate')
    )
    : undefined;
  const withholdingWarning = withholdingExpired
    ? `${t('WithholdingTaxApprovalExpired')}: ${formatDate_DD_MM_YYYY(withholdingTaxApprovalValidUntil)}`
    : withholdingExpiresWithinMonth
      ? `${t('WithholdingTaxApprovalExpiresInOneMonth')}: ${formatDate_DD_MM_YYYY(withholdingTaxApprovalValidUntil)}`
      : undefined;

  const renderRangeSwitch = () => (
    <label className={`${tb.switchRow} ${tb[direction] || ''}`} dir={direction}>
      <span>{t('DateRange')}</span>
      <input type="checkbox" checked={isRange} onChange={handleRangeChange} />
      <span className={tb.switchTrack} aria-hidden="true" />
    </label>
  );

  const renderVatDateToolbar = () => {
    if (!showPanelDates) return null;
    return (
      <div className="va-date-toolbar" dir={direction}>
        {renderRangeSwitch()}
        {isRange ? (
          <div className={tb.dateSelectRange}>
            <div className={tb.dateSelect}>
              <FilterMonthYearPicker
                year={startDate}
                month={startDate}
                onYearChange={(newYear) => handleStartDateChangeNew(newYear, 'year')}
                onMonthChange={(newMonth) => handleStartDateChangeNew(newMonth, 'month')}
                labelYear={t('StartYear')}
                labelMonth={t('StartMonth')}
                direction={direction}
              />
            </div>
            <div className={tb.rangeDivider} />
            <div className={tb.dateSelect}>
              <FilterMonthYearPicker
                year={endDate}
                month={endDate}
                onYearChange={(newYear) => handleEndDateChangeNew(newYear, 'year')}
                onMonthChange={(newMonth) => handleEndDateChangeNew(newMonth, 'month')}
                labelYear={t('EndYear')}
                labelMonth={t('EndMonth')}
                direction={direction}
              />
            </div>
          </div>
        ) : isBiMonthly ? (
          <>
            <button type="button" className={tb.periodSelect} onClick={() => setShowBiMonth(true)}>
              <CalendarTodayOutlinedIcon />
              <span className={tb.periodSelectValue}>
                <span>{formatDateToYearMonth(biStartDate.toISOString())}</span>
                <span> - </span>
                <span>{formatDateToYearMonth(biEndDate.toISOString())}</span>
              </span>
            </button>
            <BiMonthlyPeriodModal
              visible={showBiMonth}
              onClose={() => setShowBiMonth(false)}
              date={biStartDate}
              t={t}
              onDateChange={(start, end) => handleBiMonthDate(start, end)}
            />
          </>
        ) : (
          <div className={tb.dateSelect}>
            <FilterMonthYearPicker
              year={date}
              month={date}
              onYearChange={(newYear) => handleDateChangeNew(newYear, 'year')}
              onMonthChange={(newMonth) => handleDateChangeNew(newMonth, 'month')}
              labelYear={t('Year')}
              labelMonth={t('Month')}
              direction={direction}
            />
          </div>
        )}
      </div>
    );
  };

  const renderAdvancesDateToolbar = () => {
    if (!showPanelDates) return null;
    return (
      <div className="va-date-toolbar" dir={direction}>
        {renderRangeSwitch()}
        {isRange ? (
          <div className={tb.dateSelectRange}>
            <div className={tb.dateSelect}>
              <FilterMonthYearPicker
                year={advancedStartDate}
                month={advancedStartDate}
                onYearChange={(newYear) => handleAdvancedStartDateChangeNew(newYear, 'year')}
                onMonthChange={(newMonth) => handleAdvancedStartDateChangeNew(newMonth, 'month')}
                labelYear={t('AdvancedStartYear')}
                labelMonth={t('AdvancedStartMonth')}
                direction={direction}
              />
            </div>
            <div className={tb.rangeDivider} />
            <div className={tb.dateSelect}>
              <FilterMonthYearPicker
                year={advancedEndDate}
                month={advancedEndDate}
                onYearChange={(newYear) => handleAdvancedEndDateChangeNew(newYear, 'year')}
                onMonthChange={(newMonth) => handleAdvancedEndDateChangeNew(newMonth, 'month')}
                labelYear={t('AdvancedEndYear')}
                labelMonth={t('AdvancedEndMonth')}
                direction={direction}
              />
            </div>
          </div>
        ) : isAdvancedBiMonthly ? (
          <>
            <button type="button" className={tb.periodSelect} onClick={() => setShowBiMonth(true)}>
              <CalendarTodayOutlinedIcon />
              <span className={tb.periodSelectValue}>
                <span>{formatDateToYearMonth(advancedBiStartDate.toISOString())}</span>
                <span> - </span>
                <span>{formatDateToYearMonth(advancedBiEndDate.toISOString())}</span>
              </span>
            </button>
            <BiMonthlyPeriodModal
              visible={showBiMonth}
              onClose={() => setShowBiMonth(false)}
              date={advancedBiStartDate}
              t={t}
              onDateChange={(start, end) => handleAdvancedBiMonthDate(start, end)}
            />
          </>
        ) : (
          <div className={tb.dateSelect}>
            <FilterMonthYearPicker
              year={advancedDate}
              month={advancedDate}
              onYearChange={(newYear) => handleAdvancedDateChangeNew(newYear, 'year')}
              onMonthChange={(newMonth) => handleAdvancedDateChangeNew(newMonth, 'month')}
              labelYear={t('AdvancedMonthYear')}
              labelMonth={t('AdvancedMonth')}
              direction={direction}
            />
          </div>
        )}
      </div>
    );
  };

  const formatNiAmount = (value) => (value === null || value === undefined ? '—' : `${beautifyNumber(value)} ₪`);

  const renderNationalInsurancePanel = () => {
    const summary = nationalInsuranceSummary || {};
    const personalSourceLabel = summary.personalCurrentPaymentSource === 'followUp'
      ? t('NIFromFollowUp')
      : summary.personalCurrentPaymentSource === 'nationalInsurance'
        ? t('NIFromFile')
        : undefined;
    return (
      <article className="va-panel va-ni-panel va-area-ni">
        <div className="va-panel__heading">
          <h2>{t('NationalInsurance')}</h2>
          {nationalInsurancePeriodLabel ? (
            <span className="va-panel__period">{nationalInsurancePeriodLabel}</span>
          ) : null}
          {typeof onOpenNationalInsurance === 'function' ? (
            <Tooltip title={t('NIManage')}>
              <button type="button" className="va-ni-panel__manage" onClick={onOpenNationalInsurance} aria-label={t('NIManage')}>
                <UploadFileOutlinedIcon />
              </button>
            </Tooltip>
          ) : null}
        </div>
        <div className="va-ni-grid">
          <FinanceStatCard
            layout="kpi"
            variant="insurance"
            label={t('NIPersonalCurrent')}
            value={formatNiAmount(summary.personalCurrentPayment)}
            subtitle={personalSourceLabel}
            onClick={onOpenNationalInsurance}
            icon={<HealthAndSafetyOutlinedIcon />}
          />
          <FinanceStatCard
            layout="kpi"
            variant="insurance"
            label={t('NIWithholdingToPay')}
            value={formatNiAmount(summary.niWithholdingToPay)}
            onClick={onOpenReportingFollowUp}
            icon={<GroupsOutlinedIcon />}
          />
          <FinanceStatCard
            layout="kpi"
            variant="insurance"
            label={t('NIIncomeTaxWithholdingReported')}
            value={formatNiAmount(summary.withholdingReportedToPay)}
            onClick={onOpenReportingFollowUp}
            icon={<RequestQuoteOutlinedIcon />}
          />
        </div>
      </article>
    );
  };

  const renderSalaryCard = (key, label, icon) => {
    const cardProps = { layout: 'kpi', variant: 'salary', label, icon, onClick: onOpenSalary };
    if (!salaryOverview) {
      return salaryLoading
        ? <FinanceStatCard {...cardProps} value="…" subtitle={t('Loading')} className="finance-stat-card--muted" />
        : <FinanceStatCard {...cardProps} value="—" warning={t('SalaryLoadFailed')} />;
    }
    const summary = salaryOverview[key];
    if (!summary?.isConfigured) {
      return (
        <FinanceStatCard
          {...cardProps}
          value={t('SalaryNotConfiguredShort')}
          valueClassName="is-muted"
          subtitle={`+ ${t('SalaryClickToConfigure')}`}
          className="finance-stat-card--setup"
        />
      );
    }
    const source = salaryOverview.settings?.[key];
    const codesCount = source?.codes?.length || 0;
    const sourceSummary = source?.sourceType === SALARY_SOURCE.SORT_CODES
      ? t('SalarySortCodesCount', { count: codesCount })
      : t('SalaryAccountCardsCount', { count: codesCount });
    const missingCount = summary.missingMonths?.length || 0;
    return (
      <FinanceStatCard
        {...cardProps}
        value={formatNiAmount(summary.periodTotal)}
        warning={missingCount > 0 ? `${t('SalaryMissingMonthsCount', { count: missingCount })} · ${sourceSummary}` : undefined}
        subtitle={`✓ ${t('SalaryAllMonthsFilled')} · ${sourceSummary}`}
      />
    );
  };

  const renderSalaryMonthsCard = () => {
    const cardProps = { layout: 'kpi', variant: 'salary', label: t('SalaryMissingMonthsLabel'), icon: <EventBusyOutlinedIcon />, onClick: onOpenSalary };
    if (!salaryOverview) {
      return (
        <FinanceStatCard
          {...cardProps}
          value={salaryLoading ? '…' : '—'}
          subtitle={salaryLoading ? t('Loading') : undefined}
          className="finance-stat-card--muted"
        />
      );
    }
    const configured = ['salaries', 'socialBenefits'].filter((key) => salaryOverview[key]?.isConfigured);
    if (configured.length === 0) {
      return (
        <FinanceStatCard
          {...cardProps}
          value={t('SalaryNotConfiguredShort')}
          valueClassName="is-muted"
          subtitle={`+ ${t('SalaryClickToConfigure')}`}
          className="finance-stat-card--setup"
        />
      );
    }
    const missing = [...new Set(configured.flatMap((key) => salaryOverview[key].missingMonths || []))]
      .sort((a, b) => {
        const [am, ay] = a.split('/').map(Number);
        const [bm, by] = b.split('/').map(Number);
        return ay - by || am - bm;
      });
    if (missing.length === 0) {
      return <FinanceStatCard {...cardProps} value="0" subtitle={`✓ ${t('SalaryAllMonthsFilled')}`} />;
    }
    return (
      <FinanceStatCard
        {...cardProps}
        value={String(missing.length)}
        valueClassName="is-negative"
        warning={missing.slice(0, 3).join(', ') + (missing.length > 3 ? '…' : '')}
      />
    );
  };

  const renderSalaryPanel = () => (
    <article className="va-panel va-ni-panel va-area-salary">
      <div className="va-panel__heading">
        <h2>{t('SalaryTitle')}</h2>
        {salaryPeriodLabel ? <span className="va-panel__period">{salaryPeriodLabel}</span> : null}
        {typeof onOpenSalary === 'function' ? (
          <Tooltip title={t('SalaryManage')}>
            <button type="button" className="va-ni-panel__manage" onClick={onOpenSalary} aria-label={t('SalaryManage')}>
              <TuneOutlinedIcon />
            </button>
          </Tooltip>
        ) : null}
      </div>
      <div className="va-ni-grid va-salary-grid">
        {renderSalaryCard('salaries', t('SalarySalaries'), <BadgeOutlinedIcon />)}
        {renderSalaryCard('socialBenefits', t('SalarySocialBenefits'), <VolunteerActivismOutlinedIcon />)}
        {renderSalaryMonthsCard()}
      </div>
    </article>
  );

  const sidePanelsCount = (showNationalInsurancePanel ? 1 : 0) + (showSalaryPanel ? 1 : 0);
  const analyticsGridClass = [
    'va-analytics-grid',
    sidePanelsCount > 0 ? 'va-analytics-grid--with-side' : '',
    sidePanelsCount > 1 ? 'va-analytics-grid--two-side' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`vat-advances__container ${direction}`} dir={direction}>
      <section className={analyticsGridClass}>
        <article className="va-panel va-area-summary">
          <div className="va-panel__heading">
            <h2>{t('TotalSummary') || 'סיכום כולל'}</h2>
          </div>
          <div className={`va-kpi-grid${hideAdvancesColumn ? ' va-kpi-grid--two-rows' : ''}`}>
            <Tooltip title={t('NavigateToIncomes')}>
              <span>
                <FinanceStatCard
                  layout="kpi"
                  variant="income"
                  label={t('Incomes')}
                  value={`${beautifyNumber(incomesAmount)} ₪`}
                  onClick={() => viewDocumentDetails('Incomes')}
                  icon={<TrendingDownOutlinedIcon />}
                />
              </span>
            </Tooltip>
            <Tooltip title={t('NavigateToExpenses')}>
              <span>
                <FinanceStatCard
                  layout="kpi"
                  variant="expense"
                  label={t('Expenses')}
                  value={`${beautifyNumber(expensesAmount)} ₪`}
                  onClick={() => viewDocumentDetails('Expenses')}
                  icon={<TrendingUpOutlinedIcon />}
                />
              </span>
            </Tooltip>
            <FinanceStatCard
              layout="kpi"
              variant="profit"
              label={t('financialProfit')}
              value={`${beautifyNumber(profitAmount)} ₪`}
              valueClassName={Number(profitAmount) < 0 ? 'is-negative' : ''}
              icon={<ShowChartOutlinedIcon />}
            />
            <Tooltip title={t('NavigateToPurchaseOfEquipment')}>
              <span>
                <FinanceStatCard
                  layout="kpi"
                  variant="equipment"
                  label={t('PurchaseExpenses')}
                  value={`${beautifyNumber(purchaseOfEquipmentExpensesAmount)} ₪`}
                  onClick={() => viewSortCodeReport('רכישת ציוד', {
                    startDate,
                    endDate,
                    isMonthly,
                    startYearDate,
                    endYearDate,
                  })}
                  icon={<PrecisionManufacturingOutlinedIcon />}
                />
              </span>
            </Tooltip>
            {!hideAdvancesColumn && (
              <>
                <Tooltip title={t('NavigateToReceiptsAndPayments')}>
                  <span>
                    <FinanceStatCard
                      layout="kpi"
                      variant="payment"
                      label={t('paymentWithholdingTax')}
                      value={beautifyNumber(paymentWithholdingTax)}
                      onClick={() => viewPaymentAcceptanceDetails('PaymentAcceptances')}
                      icon={<PaymentsOutlinedIcon />}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t('WithholdingTaxPercentage')}>
                  <span>
                    <FinanceStatCard
                      layout="kpi"
                      variant="withholding"
                      label={t('WithholdingTaxPercentage')}
                      value={withholdingValue}
                      warning={withholdingWarning}
                      subtitle={withholdingSubtitle}
                      icon={<PercentOutlinedIcon />}
                    />
                  </span>
                </Tooltip>
              </>
            )}
          </div>
        </article>

        {showNationalInsurancePanel && renderNationalInsurancePanel()}
        {showSalaryPanel && renderSalaryPanel()}

        <article className="va-panel va-area-chart">
          <StatusOverviewChart
            caseId={client?.caseId}
            accountantId={client?.accountantId}
            token={token}
            startDate={chartStartDate}
            endDate={chartEndDate}
            onlyProcessed={onlyProcessedDocuments}
            fallbackIncomes={incomesAmount}
            fallbackExpenses={expensesAmount}
            fallbackProfit={profitAmount}
            fallbackLabel={computeVatPeriodLabel()}
            beautifyNumber={beautifyNumber}
            t={t}
          />
        </article>
      </section>

      <section className="va-tax-grid">
        {!hideVatColumn && (
          <Box className="vat-panel">
            <div className="panel-section-title">
              <Receipt />
              <span>{t('VATReports')}</span>
              {canSendChatMessage && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ChatIcon sx={{ fontSize: 'var(--xtram-panel-icon-sm, 16px)', color: '#fff' }} />}
                  onClick={() => handleOpenSendPaymentMessage('vat')}
                  className="panel-send-btn"
                >
                  {t('SendVatPaymentMessage') || t('SendToChat')}
                </Button>
              )}
            </div>
            <div className="panel-body">
              {renderVatDateToolbar()}
              <VatTable
                direction={direction}
                headerStyle={headerStyle}
                t={t}
                beautifyNumber={beautifyNumber}
                netVatAmount={netVatAmount}
                incomesVatDetailed={incomesVatDetailed}
                expensesVatDetailed={expensesVatDetailed}
                equipmentVatDetailed={equipmentVatDetailed}
                purchaseOfEquipmentExpensesVatAmount={purchaseOfEquipmentExpensesVatAmount}
                expensesVatAmount={expensesVatAmount}
                incomesVatAmount={incomesVatAmount}
                reportTax={reportTax}
                downloadVATReport={downloadVATReport}
                permissionsState={permissionsState}
                userTypeContext={userTypeContext}
                handleShowVatModal={handleShowVatModal}
                VatTooltip={VatTooltip}
              />
            </div>
          </Box>
        )}

        {!hideAdvancesColumn && (
          <Box className={`advances-panel${hideVatColumn ? ' advances-panel--full' : ''}`}>
            <div className="panel-section-title">
              <AccountBalance />
              <span>{t('IncomeTaxAdvanceReport')}</span>
              {token && client?.caseId && (
                <Button
                  size="small"
                  variant="outlined"
                  className="panel-manage-btn"
                  onClick={() => setYearlyTaxAdvanceModalOpen(true)}
                >
                  {t('ManageYearlyTaxAdvance')}
                </Button>
              )}
              {canSendChatMessage && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ChatIcon sx={{ fontSize: 'var(--xtram-panel-icon-sm, 16px)', color: '#fff' }} />}
                  onClick={() => handleOpenSendPaymentMessage('advances')}
                  className="panel-send-btn"
                >
                  {t('SendAdvancesPaymentMessage') || t('SendToChat')}
                </Button>
              )}
            </div>
            <div className="panel-body">
              {/* <div className="panel-stats-grid panel-stats-grid--advances">
                <Tooltip title={t('AdvanceDynamicCalculator')}>
                  <span>
                    <FinanceStatCard
                      variant="calculator"
                      label={t('AdvanceDynamicCalculator')}
                      value={null}
                      onClick={() => viewAdvanceCalculator('AdvanceDynamicCalculator')}
                      icon={<CalculateOutlinedIcon sx={{ fontSize: 'var(--xtram-panel-icon-sm, 18px)' }} />}
                    />
                  </span>
                </Tooltip>
              </div> */}
              {renderAdvancesDateToolbar()}
              {client.isPartners && partners.length > 0 && (
                <PartnerSelectorContainer>
                  <StyledFormControl size="small">
                    <InputLabel>{t('ReportForPartner')}</InputLabel>
                    <Select
                      value={selectedPartnerId}
                      onChange={handlePartnerChange}
                      label={t('ReportForPartner')}
                      disabled={partnersLoading}
                    >
                      <MenuItem value="all">{t('AllPartners')}</MenuItem>
                      {partners.map((partner) => (
                        <MenuItem key={partner.id} value={partner.id}>
                          {partner.name} ({partner.equity}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                  <Box className="panel-partner-stats">
                    <FinanceStatCard
                      variant="profit"
                      label={t('BusinessTurnoverInILS')}
                      value={`${beautifyNumber(partnerBusinessTurnover)} ₪`}
                      icon={<ShowChartOutlinedIcon sx={{ fontSize: 'var(--xtram-panel-icon-sm, 16px)' }} />}
                    />
                    <FinanceStatCard
                      variant="payment"
                      label={t('AcceptedWithholdingTax')}
                      value={`${beautifyNumber(partnerAcceptedWithholdingTotal)} ₪`}
                      icon={<PaymentsOutlinedIcon sx={{ fontSize: 'var(--xtram-panel-icon-sm, 16px)' }} />}
                    />
                  </Box>
                </PartnerSelectorContainer>
              )}
              <AdvancesTable
                direction={direction}
                headerStyle={headerStyle}
                t={t}
                beautifyNumber={beautifyNumber}
                client={client}
                calculatedValues={calculatedValues}
                witholdingIncomesNoVat={witholdingIncomesNoVat || 0}
                downloadWithholdingTaxReport={handleDownloadWithholdingTaxReportWithPartner}
                advanceReportTax={advanceReportTax || {}}
                permissionsState={permissionsState}
                userTypeContext={userTypeContext}
                handleOpenWithholdingTaxModal={handleOpenWithholdingTaxModalWithPartner}
                Constants={Constants}
                partners={partners}
                selectedPartnerId={selectedPartnerId}
                partnerCalculatedValues={partnerCalculatedValues}
                isSubordinateClient={isSubordinateClient}
                mainClientData={mainClientData}
                isMainWithSubordinates={isMainWithSubordinates}
                subordinateClients={subordinateClients}
              />
            </div>
          </Box>
        )}
      </section>

      <YearlyTaxAdvanceModal
        open={yearlyTaxAdvanceModalOpen}
        onClose={() => setYearlyTaxAdvanceModalOpen(false)}
        caseId={client?.caseId}
        token={token}
        t={t}
      />

      <SendPaymentMessageModal
        open={sendPaymentMessageMode === 'vat'}
        onClose={() => setSendPaymentMessageMode(null)}
        t={t}
        direction={direction}
        clientName={client?.name || ''}
        accountantName={accountantName}
        caseId={client?.caseId || ''}
        periodLabel={computeVatPeriodLabel()}
        defaultNetVat={Number(netVatAmount) || 0}
        defaultIncomeTaxAdvances={0}
        includeVat={true}
        includeAdvances={false}
        onSend={handleSendPaymentMessage}
        onSendWhatsApp={handleSendPaymentMessageWhatsApp}
      />

      <SendPaymentMessageModal
        open={sendPaymentMessageMode === 'advances'}
        onClose={() => setSendPaymentMessageMode(null)}
        t={t}
        direction={direction}
        clientName={client?.name || ''}
        accountantName={accountantName}
        caseId={client?.caseId || ''}
        periodLabel={computeAdvancesPeriodLabel()}
        defaultNetVat={0}
        defaultIncomeTaxAdvances={Number(calculatedValues?.computed?.IncomeTaxAdvances) || 0}
        includeVat={false}
        includeAdvances={true}
        onSend={handleSendPaymentMessage}
        onSendWhatsApp={handleSendPaymentMessageWhatsApp}
      />
    </div>
  );
};

VatAdvancesSection.propTypes = {
  fetchLoading: PropTypes.bool,
  client: PropTypes.object,
  direction: PropTypes.string,
  headerStyle: PropTypes.object,
  t: PropTypes.func.isRequired,
  beautifyNumber: PropTypes.func.isRequired,
  netVatAmount: PropTypes.number,
  incomesVatDetailed: PropTypes.object,
  expensesVatDetailed: PropTypes.object,
  equipmentVatDetailed: PropTypes.object,
  purchaseOfEquipmentExpensesVatAmount: PropTypes.number,
  expensesVatAmount: PropTypes.number,
  incomesVatAmount: PropTypes.number,
  reportTax: PropTypes.object,
  downloadVATReport: PropTypes.func,
  permissionsState: PropTypes.object,
  userTypeContext: PropTypes.any,
  handleShowVatModal: PropTypes.func,
  VatTooltip: PropTypes.elementType,
  calculatedValues: PropTypes.shape({
    computed: PropTypes.object,
    reported: PropTypes.object,
  }),
  witholdingIncomesNoVat: PropTypes.number,
  downloadWithholdingTaxReport: PropTypes.func,
  advanceReportTax: PropTypes.object,
  handleOpenWithholdingTaxModal: PropTypes.func,
  Constants: PropTypes.object,
  // Date-related props
  isRange: PropTypes.bool,
  isBiMonthly: PropTypes.bool,
  isAdvancedBiMonthly: PropTypes.bool,
  date: PropTypes.object,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  biStartDate: PropTypes.object,
  biEndDate: PropTypes.object,
  advancedDate: PropTypes.object,
  advancedStartDate: PropTypes.object,
  advancedEndDate: PropTypes.object,
  advancedBiStartDate: PropTypes.object,
  advancedBiEndDate: PropTypes.object,
  showBiMonth: PropTypes.bool,
  setShowBiMonth: PropTypes.func,
  handleDateChangeNew: PropTypes.func,
  handleStartDateChangeNew: PropTypes.func,
  handleEndDateChangeNew: PropTypes.func,
  handleBiMonthDate: PropTypes.func,
  handleAdvancedDateChangeNew: PropTypes.func,
  handleAdvancedStartDateChangeNew: PropTypes.func,
  handleAdvancedEndDateChangeNew: PropTypes.func,
  handleAdvancedBiMonthDate: PropTypes.func,
  formatDateToYearMonth: PropTypes.func,
  // Separate range controls for each section
  isVatRange: PropTypes.bool,
  isAdvancesRange: PropTypes.bool,
  handleVatRangeChange: PropTypes.func,
  handleAdvancesRangeChange: PropTypes.func,
  // Additional props for FinancialModal and ActionCards
  incomesAmount: PropTypes.number,
  expensesAmount: PropTypes.number,
  purchaseOfEquipmentExpensesAmount: PropTypes.number,
  profitAmount: PropTypes.number,
  paymentWithholdingTax: PropTypes.number,
  viewDocumentDetails: PropTypes.func,
  viewSortCodeReport: PropTypes.func,
  viewPaymentAcceptanceDetails: PropTypes.func,
  // New props for withholding tax
  withholdingTaxPercentage: PropTypes.number,
  withholdingTaxApprovalValidUntil: PropTypes.object,
  // Additional props for SortCodeReport navigation
  isMonthly: PropTypes.bool,
  startYearDate: PropTypes.object,
  endYearDate: PropTypes.object,
  // Partners props
  token: PropTypes.string,
  partners: PropTypes.array,
  calculatePartnerValues: PropTypes.func,
  partnerCalculatedValues: PropTypes.object,
  hideVatPanel: PropTypes.bool,
  // Chat
  handleSendTextMessageToChat: PropTypes.func,
  accountantName: PropTypes.string,
  onlyProcessedDocuments: PropTypes.bool,
  showNationalInsurancePanel: PropTypes.bool,
  nationalInsuranceSummary: PropTypes.shape({
    personalCurrentPayment: PropTypes.number,
    personalCurrentPaymentSource: PropTypes.string,
    niWithholdingToPay: PropTypes.number,
    withholdingReportedToPay: PropTypes.number,
  }),
  nationalInsurancePeriodLabel: PropTypes.string,
  onOpenNationalInsurance: PropTypes.func,
  onOpenReportingFollowUp: PropTypes.func,
  showSalaryPanel: PropTypes.bool,
  salaryOverview: PropTypes.object,
  salaryLoading: PropTypes.bool,
  salaryPeriodLabel: PropTypes.string,
  onOpenSalary: PropTypes.func,
};

export default VatAdvancesSection;
